import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FaWhatsapp } from "react-icons/fa";
import { waLink } from "@/lib/brand";
import { useT } from "@/lib/i18n";

// Keys are stable for both languages — used to build JSON-LD too.
export const FAQ_KEYS = [
  ["faq.q1", "faq.a1"],
  ["faq.q2", "faq.a2"],
  ["faq.q3", "faq.a3"],
  ["faq.q4", "faq.a4"],
  ["faq.q5", "faq.a5"],
  ["faq.q6", "faq.a6"],
  ["faq.q7", "faq.a7"],
  ["faq.q8", "faq.a8"],
  ["faq.q9", "faq.a9"],
  ["faq.q10", "faq.a10"],
];

export default function FAQ() {
  const { t } = useT();

  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 md:py-32 bg-[#0B1733]">
      <div className="absolute inset-x-0 top-0 divider-gold" />
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-4"
          >
            <div className="text-xs tracking-[0.3em] text-gold uppercase mb-4">{t("faq.eyebrow")}</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-white leading-tight mb-6">
              {t("faq.title.1")}{" "}
              <span className="italic text-gold">{t("faq.title.2")}</span>
            </h2>
            <p className="text-slate-400 leading-relaxed mb-8">{t("faq.blurb")}</p>
            <a
              href={waLink(t("faq.cta.msg"))}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="faq-whatsapp-cta"
              className="inline-flex items-center gap-3 border border-gold/50 hover:border-gold hover:bg-gold/5 text-gold font-medium px-6 py-3 rounded-full transition-[color,background-color,border-color,box-shadow,transform]"
            >
              <FaWhatsapp className="h-4 w-4" />
              {t("faq.cta")}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-8"
            data-testid="faq-accordion"
          >
            <Accordion type="single" collapsible className="space-y-3">
              {FAQ_KEYS.map(([qk, ak], i) => (
                <AccordionItem
                  key={qk}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border border-gold/20 rounded-2xl bg-navy-800/40 hover:border-gold/50 transition-colors px-6 data-[state=open]:bg-navy-800/70 data-[state=open]:border-gold/60"
                >
                  <AccordionTrigger className="py-5 text-left font-serif text-lg md:text-xl text-white hover:text-gold hover:no-underline [&[data-state=open]>svg]:text-gold">
                    {t(qk)}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300 leading-relaxed text-[15px] pb-6 pr-2">
                    {t(ak)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
